import { store } from "../redux/store/Store";
import { setWebSocketConnectionStatus } from "../redux/actions/Status.Actions";
import AmplifyAPIWrapper from "../API/AmplifyAPIWrapper";
import { onMessage } from "./WebsocketHandlers";
import { AlertDialog } from "src/components/AlertDialog";
import { ThemeProvider } from "@mui/material/styles";
import darkTheme from "src/themes/oc-dark-default";
import { createRoot } from "react-dom/client";
import { subscribeToDispatchDeskFlights, fetchEventsForDispatchDeskFlights } from "src/utils/dispatchDeskOpsLinkSync";

enum WsReadyState {
  "CONNECTING" = 0, //	Socket has been created. The connection is not yet open.
  "OPEN" = 1, //	The connection is open and ready to communicate.
  "CLOSING" = 2, //The connection is in the process of closing.
  "CLOSED" = 3, //	The connection is closed or couldn't be opened.
}

class WsConnection {
  public ws: WebSocket | null = null;
  public connectedAt: number | null = null;
  public onOpen: (e: Event) => void | null;
  public onMessage: (e: MessageEvent) => void;
  public onClose: (e: Event) => void;
  public onError: (e: Event) => void;

  public get active(): boolean {
    return this.ws != null && (this.ws.readyState === WsReadyState.OPEN || this.ws.readyState === this.ws.CONNECTING);
  }
  public get age(): number {
    if (this.connectedAt) {
      return new Date()?.valueOf() - this.connectedAt;
    }
    return 3.154e13;
  }

  constructor(
    onOpen: (e: Event) => void,
    onMessage: (e: MessageEvent) => void,
    onClose: (e: Event) => void,
    onError: (e: Event) => void
  ) {
    this.onOpen = onOpen;
    this.onMessage = onMessage;
    this.onClose = onClose;
    this.onError = onError;
  }

  public async connect(): Promise<void> {
    const websocketURL: string | null = JSON.parse(localStorage.getItem("amplify_config") ?? "").WebsocketURL;
    const token = await AmplifyAPIWrapper.getJwtToken();
    const url = websocketURL + "?token=" + token;
    this.ws = new WebSocket(url);
    this.ws.onopen = (ev: Event) => {
      this.connectedAt = new Date()?.valueOf();
      if (this.onOpen) {
        this.onOpen(ev);
      }
    };
    this.ws.onmessage = (ev: MessageEvent) => {
      if (this.onMessage) {
        this.onMessage(ev);
      }
    };
    this.ws.onclose = (ev: CloseEvent) => {
      if (this.onClose) {
        this.onClose(ev);
      }
    };
    this.ws.onerror = (ev: Event) => {
      if (this.onError) {
        this.onError(ev);
      }
    };
  }

  public send(data: any): void {
    this.ws?.send(JSON.stringify(data));
  }

  public sendRaw(data: string | ArrayBuffer | SharedArrayBuffer | Blob | ArrayBufferView): void {
    this.ws?.send(data);
  }

  public close(): void {
    if (this.ws) {
      this.ws.close();
    }
    this.connectedAt = null;
  }
}
let hasFailed = false;

class WebsocketClient {
  private static wsa: WsConnection;
  private static wsb: WsConnection;
  private static primary: WsConnection;

  private static heartBeatTimeHandle: number | null = null;
  private static readonly heartBeatInterval = 25000;
  private static readonly recycleWsAfter = 1.8e6;

  public static get isConnected(): boolean {
    if ((this.wsa && this.wsa.active === true) || (this.wsb && this.wsb.active === true)) {
      return true;
    }
    return false;
  }

  private static heartbeatHandler(): void {
    if (WebsocketClient.primary.active === true) {
      WebsocketClient.primary.send({ action: "heartbeat" });
    } else {
      console.warn("heartbeat failed: no active connection");
    }

    // if the primary connection is too old establish the other one
    // on the other connection it will switch the primary over and kill the other one
    if (WebsocketClient.primary.age > WebsocketClient.recycleWsAfter) {
      console.log("recycling connection");
      if (WebsocketClient.primary === WebsocketClient.wsa) {
        WebsocketClient.wsb.connect();
      } else if (WebsocketClient.primary === WebsocketClient.wsb) {
        WebsocketClient.wsa.connect();
      }
    }

    //double check the connection, we should never not have a connection
    if (WebsocketClient.primary.active === false) {
      WebsocketClient.failHard();
    }
  }

  public static init(): void {
    if (WebsocketClient.heartBeatTimeHandle !== null) {
      window.clearInterval(WebsocketClient.heartBeatTimeHandle);
    }
    WebsocketClient.heartBeatTimeHandle = window.setInterval(() => {
      this.heartbeatHandler();
    }, WebsocketClient.heartBeatInterval);

    const eventOpen = (event: Event): void => {
      const inboundConnection = event.currentTarget as any;
      if (inboundConnection === WebsocketClient.wsa.ws) {
        WebsocketClient.primary = WebsocketClient.wsa;
        WebsocketClient.wsb.close();
      } else if (inboundConnection === WebsocketClient.wsb.ws) {
        WebsocketClient.primary = WebsocketClient.wsb;
        WebsocketClient.wsa.close();
      }

      store.dispatch(setWebSocketConnectionStatus(true));

      // Re-subscribe to dispatch desk flights on every new connection
      // (initial connect and connection recycle).  Also fetch events so the
      // unacknowledged counter is populated even if the App.tsx .then() ran
      // before the WS was connected.
      subscribeToDispatchDeskFlights();
      fetchEventsForDispatchDeskFlights();
    };

    const eventMessage = (msg: MessageEvent): void => {
      // only action if it's the primary connection to prevent double handling
      if ((msg.currentTarget as any) === WebsocketClient.primary.ws) {
        Promise.resolve(onMessage(msg));
      }
    };

    const eventClose = (event: Event): void => {
      // closes happen normally, nothing really to do here
    };

    const eventError = (event: Event): void => {
      console.error(event);
      WebsocketClient.failHard();
    };

    WebsocketClient.wsa = new WsConnection(eventOpen, eventMessage, eventClose, eventError);
    WebsocketClient.wsb = new WsConnection(eventOpen, eventMessage, eventClose, eventError);
    this.primary = this.wsa;
    Promise.resolve(WebsocketClient.wsa.connect());
  }

  /**
   * Send a message through the primary WebSocket connection.
   * No-op if no active connection exists or the socket is still connecting.
   */
  public static send(data: any): void {
    if (WebsocketClient.primary?.ws?.readyState === WsReadyState.OPEN) {
      WebsocketClient.primary.send(data);
    }
  }

  public static close(): void {
    if (WebsocketClient.wsa && WebsocketClient.wsa.active) {
      WebsocketClient.wsa.close();
    }
    if (WebsocketClient.wsb && WebsocketClient.wsb.active) {
      WebsocketClient.wsb.close();
    }
  }

  // New error message
  private static failHard(): void {
    //Lets the error message only trigger once
    if (!hasFailed) {
      hasFailed = true;

      const overlayContainer = document.createElement("div");
      overlayContainer.style.position = "fixed";
      overlayContainer.style.top = "0";
      overlayContainer.style.left = "0";
      overlayContainer.style.width = "100%";
      overlayContainer.style.height = "100%";
      overlayContainer.style.zIndex = "1000";

      document.body.appendChild(overlayContainer);
      const element = (
        <ThemeProvider theme={darkTheme}>
          <AlertDialog title="" msg="" buttons={[]} />
        </ThemeProvider>
      );
      const root = createRoot(overlayContainer);
      root.render(element);
    }
  }
}

export default WebsocketClient;
